import { useSnackbar } from "notistack";
import { FC, useEffect, useState } from "react";

import { Button, FormControl, MenuItem, Select, Stack, Switch, TextField } from "@mui/material";

import { api } from "../../../../../utils";
import { IError } from "../../../../../models/ReportPageModels";
import { ReportUserDisplayModel } from "../ViewReportUser/ViewReportUser";
import { BillingCompanyModel } from "../CreateReportUser/CreateReportUser";

interface AdminReportUsers {
    data: AdminReportUsersData;
    error: IError;
}
interface AdminReportUsersData {
    users: ReportUserModel[];
}

interface ReportUserModel {
    id: number;
    username: string;
    password: string;
    companyFK: number;
    imagesType: string;
    imagesDetails: string;
    boothIDFilter: string;
    useReview: number;
    reviewImagePrefix: string;
}

interface EditReportUserRequest {
    id: number;
    username: string;
    password: string;
    companyFK: number;
    imagesType: string;
    imagesDetails: string;
    boothIDFilter: string;
    useReview: boolean;
    reviewImagePrefix: string;
}
interface EditReportUserResponse {
    data: EditReportUserData;
    error: IError;
}
interface EditReportUserData {
    status: string;
}

interface AdminGetBillingCompaniesResponse {
    data: BillingCompaniesData;
    error: IError;
}

interface BillingCompaniesData {
    companies: BillingCompanyModel[];
}


interface EditReportUserProps {
    editUser: ReportUserDisplayModel | null;
}

// TODO: Get from one place. so we don't need to edit it in two places
const allImageTypes: string[] = ["s3", "gcp", "cargiant", "smh"];

const EditReportUser: FC<EditReportUserProps> = ({ editUser }) => {
    const { enqueueSnackbar } = useSnackbar();

    const [userId, setUserId] = useState<number>(0);
    const [username, setUsername] = useState<string>("");
    const [password, setPassword] = useState<string>("");
    const [selectedCompany, setSelectedCompany] = useState<number>(0);
    const [selectedImageType, setSelectedImageType] = useState<string>("s3");
    const [imageDetails, setImageDetails] = useState<string>("");
    const [boothFilter, setBoothFilter] = useState<string>("");
    const [useReview, setUseReview] = useState<boolean>(false);
    const [reviewImagePrefix, setReviewImagePrefix] = useState<string>("");

    const [users, setUsers] = useState<ReportUserModel[]>([]);
    const [companies, setCompanies] = useState<BillingCompanyModel[]>([]);

    useEffect(() => {
        if (companies.length === 0)
            GetCompanies();
        if (users.length === 0) {
            GetUsers().then((temp: ReportUserModel[] | undefined) => {
                if (editUser === null)
                    return;
                if (temp === undefined)
                    return;

                var user = null;

                for (var i = 0; i < temp.length; i++) {
                    if (temp[i].id === editUser.id) {
                        user = temp[i]
                        break;
                    }
                }

                if (user === null)
                    return;

                setUserId(user.id);
                setUsername(user.username);
                setPassword(user.password);
                setSelectedCompany(user.companyFK);
                setSelectedImageType(user.imagesType);
                setImageDetails(user.imagesDetails);
                setBoothFilter(user.boothIDFilter);
                setUseReview(user.useReview === 1);
                setReviewImagePrefix(user.reviewImagePrefix);
            });
        }
    }, [editUser])

    const GetCompanies = async () => {
        try {
            const response: AdminGetBillingCompaniesResponse = await api.get("api/Admin/GetBillingCompanies", {
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).json();

            if (response.error == null) {
                setCompanies(response.data.companies)
            }
            else {
                enqueueSnackbar(response.error.description, { variant: "error" });
            }
        }
        catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
        }
    }

    const GetUsers = async () => {
        try {
            const response: AdminReportUsers = await api.get("api/Admin/GetDbReportUsers", {
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).json();

            if (response.error == null) {
                setUsers(response.data.users);
                return response.data.users;
            }
            else {
                enqueueSnackbar(response.error.description, { variant: "error" });
            }
        }
        catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
        }
    };

    const EditClient = async () => {
        if (userId === 0 || username === "" || password === "" || selectedCompany === 0) {
            enqueueSnackbar("Please recheck fields", { variant: "error" });
            return;
        }

        var body: EditReportUserRequest = {
            id: userId,
            username: username,
            password: password,
            companyFK: selectedCompany,
            imagesType: selectedImageType,
            imagesDetails: imageDetails,
            boothIDFilter: boothFilter,
            useReview: useReview,
            reviewImagePrefix: reviewImagePrefix
        }

        try {
            const response: EditReportUserResponse = await api.post("api/Admin/EditReportUser", {
                json: body,
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).json();

            if (response.error == null) {
                enqueueSnackbar("Edited!", { variant: "success" })
                // clear fields
                setUserId(0);
                setUsername("");
                setPassword("");
                setSelectedCompany(0);
                setSelectedImageType("s3");
                setImageDetails("");
                setBoothFilter("");
                setUseReview(false);
                setReviewImagePrefix("");

                // get new users from db
                GetUsers();
            }
            else {
                enqueueSnackbar(response.error.description, { variant: "error" });
            }
        }
        catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
        }
    }

    function SetUserId(event: any) {
        var value: number = event.target.value;
        setUserId(value);
        var user = null;

        for (var i = 0; i < users.length; i++) {
            if (users[i].id === value) {
                user = users[i]
                break;
            }
        }

        if (user === null)
            return;

        setUsername(user.username);
        setPassword(user.password);
        setSelectedCompany(user.companyFK);
        setSelectedImageType(user.imagesType);
        setImageDetails(user.imagesDetails);
        setBoothFilter(user.boothIDFilter);
        setUseReview(user.useReview === 1);
        setReviewImagePrefix(user.reviewImagePrefix);
    }

    const IsFormValid = userId !== 0 && username !== "" && password !== "" && selectedCompany !== 0;

    return (
        <Stack>
            {/* Users */}
            <Stack
                direction={"row"}
                alignItems={"center"}>
                <p style={{ width: '110px' }}>Users: </p>
                <FormControl sx={{ minWidth: 245 }}>
                    <Select
                        value={userId}
                        onChange={SetUserId}
                        size="small"
                        defaultValue={-1}>
                        <MenuItem
                            value={0}>
                            {"Select user"}
                        </MenuItem>
                        {users.map((user, index) =>
                            <MenuItem
                                key={index}
                                value={user.id}>
                                {user.username}
                            </MenuItem>
                        )}
                    </Select>
                </FormControl>
            </Stack>

            {/* Username */}
            <Stack
                direction={"row"}
                alignItems={"center"} >
                <p style={{ width: '110px' }}>Username: </p>
                <TextField
                    value={username}
                    onChange={(newValue: any) => { setUsername(newValue.target.value) }}
                    sx={{ minWidth: 245 }}
                    size="small" />
            </Stack>

            {/* Password */}
            <Stack
                direction={"row"}
                alignItems={"center"}>
                <p style={{ width: '110px' }}>Password: </p>
                <TextField
                    value={password}
                    onChange={(newValue: any) => { setPassword(newValue.target.value) }}
                    sx={{ minWidth: 245 }}
                    size="small" />
            </Stack>

            {/* Company */}
            <Stack
                direction={"row"}
                alignItems={"center"}>
                <p style={{ width: '110px' }}>Company: </p>
                <FormControl sx={{ minWidth: "245px" }}>
                    <Select
                        value={selectedCompany}
                        onChange={(newValue: any) => { setSelectedCompany(newValue.target.value); }}
                        size="small">
                        <MenuItem
                            value={0}>
                            {"Select company"}
                        </MenuItem>
                        {companies.map((company, index) =>
                            <MenuItem
                                key={index}
                                value={company.id}>
                                {company.branchName}
                            </MenuItem>
                        )}
                    </Select>
                </FormControl>
            </Stack>

            {/* ImageType */}
            <Stack
                direction={"row"}
                alignItems={"center"}>
                <p style={{ width: '110px' }}>Image type: </p>
                <FormControl sx={{ minWidth: "245px" }}>
                    <Select
                        value={selectedImageType}
                        onChange={(newValue: any) => { setSelectedImageType(newValue.target.value); }}
                        size="small">
                        {allImageTypes.map((type, index) =>
                            <MenuItem
                                key={index}
                                value={type}>
                                {type}
                            </MenuItem>
                        )}
                    </Select>
                </FormControl>
            </Stack>

            {/* ImageDetails */}
            <Stack
                direction={"row"}
                alignItems={"center"}>
                <p style={{ width: '110px' }}>Image details: </p>
                <TextField
                    value={imageDetails}
                    onChange={(newValue: any) => { setImageDetails(newValue.target.value) }}
                    sx={{ minWidth: 245 }}
                    size="small" />
            </Stack>

            {/* BoothIdFilter */}
            <Stack
                direction={"row"}
                alignItems={"center"}>
                <p style={{ width: '110px' }}>BoothId filter: </p>
                <TextField
                    value={boothFilter}
                    onChange={(newValue: any) => { setBoothFilter(newValue.target.value) }}
                    sx={{ minWidth: 245 }}
                    size="small" />
                <p style={{ color: "gray" }}> Exact BoothId or part of Boothid  in "%". Example: %_B2C%</p>
            </Stack>

            {/* Use Review */}
            <Stack
                direction={"row"}
                alignItems={"center"} >
                <p style={{ width: '110px' }}>Use Review: </p>
                <Switch
                    checked={useReview}
                    onChange={(newValue) => { setUseReview(newValue.target.checked) }}
                    size="small" />
            </Stack>

            {/* Review Image Prefix */}
            <Stack
                direction={"row"}
                alignItems={"center"}>
                <p style={{ width: '110px' }}>Review Image URL Prefix: </p>
                <TextField
                    value={reviewImagePrefix}
                    onChange={(newValue: any) => { setReviewImagePrefix(newValue.target.value) }}
                    sx={{ minWidth: 245 }}
                    size="small" />
            </Stack>

            <Button
                variant="outlined"
                onClick={() => EditClient()}
                sx={{ width: '82px' }}
                disabled={!IsFormValid}>
                Edit
            </Button>
        </Stack>
    )
}

export default EditReportUser;